import { isPlayground } from '@/pages/game/live-events/api/playgroundBackend.js';
import {
    endDate, openDate, skipDate, unskipDate,
} from '../../upcoming/api/upcomingApi.js';
import { webRequest } from '../../webRequest.js';
import { VIEW_AHEAD_DAYS } from '../model/placements.js';
import { flywheelMockRequest } from './mockFlywheels.js';

const enc = encodeURIComponent;
const NAME_MAX = 80;
const EXPERIMENT_PHASES = ['SCHEDULED', 'LIVE', 'PAUSED'];
const EXPERIMENT_PAGE = 100;
const EXPERIMENT_MAX = 1000;
const FEED_ROWS = 500;
const PUBLISHED = new Set(['SCHEDULED', 'LIVE', 'ENDED']);

const live = () => !isPlayground();
const request = (gameId, req) => (isPlayground() ? flywheelMockRequest(gameId, req) : webRequest(gameId, req));

export const FLYWHEELS_CHANGED = 'geoplay:flywheels-changed';

const announce = (gameId) => window.dispatchEvent(new CustomEvent(FLYWHEELS_CHANGED, { detail: { gameId } }));

export const listFlywheels = async (gameId) => (await request(gameId, { method: 'get', path: '/flywheels' }))?.flywheels || [];

export const createFlywheel = async (gameId, data) => {
    const flywheel = (await request(gameId, { method: 'post', path: '/flywheels', data }))?.flywheel || null;
    announce(gameId);
    return flywheel;
};

export const updateFlywheel = async (gameId, id, data) => {
    const flywheel = (await request(gameId, { method: 'put', path: `/flywheels/${enc(id)}`, data }))?.flywheel || null;
    announce(gameId);
    return flywheel;
};

export const endOccurrence = (gameId, instanceId) => endDate(gameId, instanceId);

// What the server stopped before deleting or pausing: { withdrawn, ended }.
const stoppedOf = (res) => ({ withdrawn: res?.withdrawn || 0, ended: res?.ended || 0 });

// The server takes back its published dates first and, with `endRunning`, ends the running ones.
export const deleteFlywheel = async (gameId, id, endRunning) => {
    const res = await request(gameId, { method: 'delete', path: `/flywheels/${enc(id)}`, params: { endRunning: !!endRunning } });
    announce(gameId);
    return stoppedOf(res);
};

export const listTimelineItems = async (gameId, flywheelId) => (await request(gameId, {
    method: 'get', path: '/timeline-items', params: flywheelId ? { flywheelId } : undefined,
}))?.timelineItems || [];

const copyName = (name, taken) => {
    const used = new Set(taken.map((n) => n.toLowerCase()));
    let next = `${name} (copy)`.slice(0, NAME_MAX);
    for (let i = 2; used.has(next.toLowerCase()); i += 1) next = `${name} (copy ${i})`.slice(0, NAME_MAX);
    return next;
};

// A copied Live Event is placed paused, so the copy doesn't list the same dates twice.
const copyItem = async (gameId, item, flywheelId) => (await webRequest(gameId, {
    method: 'post',
    path: '/timeline-items',
    data: {
        flywheelId,
        kind: item.kind,
        beatId: item.beatId,
        objectiveId: item.objectiveId,
        durationSeconds: item.durationSeconds,
        startAt: item.startAt,
        horizonDays: item.horizonDays,
        scheduleOverride: item.scheduleOverride,
        title: item.title,
        note: item.note,
        color: item.color,
        icon: item.icon,
        sortOrder: item.sortOrder,
        paused: item.kind === 'BEAT',
    },
}))?.timelineItem;

const copyFlywheel = async (gameId, id) => {
    const flywheels = await listFlywheels(gameId);
    const source = flywheels.find((f) => f.id === id);
    if (!source) throw new Error('This flywheel no longer exists.');
    const copy = (await webRequest(gameId, {
        method: 'post',
        path: '/flywheels',
        data: {
            name: copyName(source.name, flywheels.map((f) => f.name)),
            description: source.description,
            icon: source.icon,
            color: source.color,
            sortOrder: Math.max(-1, ...flywheels.map((f) => f.sortOrder ?? 0)) + 1,
        },
    }))?.flywheel || null;
    if (!copy) return null;
    try {
        const items = await listTimelineItems(gameId, id);
        for (const item of items) await copyItem(gameId, item, copy.id);
    } catch (e) {
        await webRequest(gameId, { method: 'delete', path: `/flywheels/${enc(copy.id)}` }).catch(() => {});
        throw e;
    }
    return copy;
};

export const duplicateFlywheel = async (gameId, id) => {
    const flywheel = live()
        ? await copyFlywheel(gameId, id)
        : (await request(gameId, { method: 'post', path: `/flywheels/${enc(id)}/duplicate` }))?.flywheel || null;
    announce(gameId);
    return flywheel;
};

export const createTimelineItem = (gameId, data) => request(gameId, { method: 'post', path: '/timeline-items', data });

export const updateTimelineItem = async (gameId, id, data) => (
    await request(gameId, { method: 'put', path: `/timeline-items/${enc(id)}`, data }))?.timelineItem || null;

export const pausePlacement = async (gameId, id, endRunning) => stoppedOf(await request(gameId, {
    method: 'put', path: `/timeline-items/${enc(id)}`, data: { paused: true, endRunning: !!endRunning },
}));

export const resumePlacement = (gameId, id) => request(gameId, { method: 'put', path: `/timeline-items/${enc(id)}`, data: { paused: false } });

export const removePlacement = async (gameId, id, endRunning) => stoppedOf(
    await request(gameId, { method: 'delete', path: `/timeline-items/${enc(id)}`, params: { endRunning: !!endRunning } }));

// Upcoming rows carry a date's state but not its timestamps; the board only reads them as flags.
const instanceFromRow = (row) => {
    const published = PUBLISHED.has(row.status);
    return {
        id: row.instanceId,
        timelineItemId: row.timelineItemId,
        beatId: row.beatId,
        occStart: row.occurrenceStart,
        publishedAt: published ? row.occurrenceStart : null,
        skippedAt: row.status === 'SKIPPED' ? row.occurrenceStart : null,
        skipReason: row.status === 'SKIPPED' ? row.statusReason || null : null,
        cancelledAt: row.status === 'CANCELLED' ? row.occurrenceStart : null,
        endedEarlyAt: null,
        autoError: row.autoError || null,
        window: published ? { startsAt: row.occurrenceStart, endsAt: row.occurrenceEnd || null } : null,
    };
};

// The records of the dates ahead, from Upcoming, and the org zone the server worked them out in.
export const listDatesAhead = async (gameId) => {
    const feed = (await request(gameId, {
        method: 'get', path: '/upcoming-live-events', params: { horizonDays: VIEW_AHEAD_DAYS, limit: FEED_ROWS },
    }))?.upcoming;
    const rows = (feed?.days || []).flatMap((d) => d.events || []).filter((row) => row.instanceId);
    return { zone: feed?.timezone || 'UTC', instances: rows.map(instanceFromRow) };
};

// Upcoming stops listing a date once it starts, so a started date is opened to read its record; that needs edit access.
export const openDates = async (gameId, dates) => {
    const results = await Promise.allSettled(dates.map((d) => openDate(gameId, d.itemId, d.startMs)));
    return {
        instances: results.filter((r) => r.status === 'fulfilled' && r.value).map((r) => r.value),
        failed: results.some((r) => r.status === 'rejected'),
    };
};

// The date's record, opened when it has none yet; `running` when it is published and inside its window.
export const openOccurrence = async (gameId, { itemId, startMs, endMs }) => {
    const inst = await openDate(gameId, itemId, startMs);
    const now = Date.now();
    const running = !!inst?.publishedAt && !inst.endedEarlyAt && !inst.cancelledAt && startMs <= now && (endMs == null || now < endMs);
    return { inst, running };
};

export const skipOccurrence = (gameId, inst, reason) => skipDate(gameId, inst.id, reason);

export const unskipOccurrence = async (gameId, instanceId) => {
    await unskipDate(gameId, instanceId);
    if (live()) return null;
    return (await request(gameId, { method: 'post', path: `/live-event-instances/${enc(instanceId)}/ship` }))?.dates || null;
};

export const getHome = async (gameId, upcomingLimit = 10) => (await request(gameId, {
    method: 'get', path: '/live-services/home', params: { upcomingLimit },
}))?.home || null;

export const listLiveEvents = async (gameId) => (await request(gameId, { method: 'get', path: '/live-events' }))?.liveEvents || [];

export const listObjectives = async (gameId) => (await request(gameId, { method: 'get', path: '/game-objectives' }))?.gameObjectives || [];

const forbidden = (e) => e?.response?.status === 403;

// Scheduled and running Live Experiments; none when the user can't see A/B testing.
export const listExperiments = async (gameId) => {
    if (!live()) return [];
    const out = [];
    let cursor = null;
    try {
        do {
            const params = new URLSearchParams(EXPERIMENT_PHASES.map((phase) => ['phase', phase]));
            params.set('limit', String(EXPERIMENT_PAGE));
            if (cursor) params.set('cursor', cursor);
            const page = await webRequest(gameId, { method: 'get', path: '/live-experiments', params });
            out.push(...(page?.experiments || []));
            cursor = page?.nextCursor || null;
        } while (cursor && out.length < EXPERIMENT_MAX);
    } catch (e) {
        if (forbidden(e)) return [];
        throw e;
    }
    return out;
};

// Tests per phase, or { noAccess: true } when the user can't see A/B testing.
export const getExperimentCounts = async (gameId) => {
    if (!live()) return {};
    try {
        return (await webRequest(gameId, { method: 'get', path: '/live-experiments/counts' }))?.counts || {};
    } catch (e) {
        if (forbidden(e)) return { noAccess: true };
        throw e;
    }
};

export const listConfigFiles = async (gameId) => (await request(gameId, { method: 'get', path: '/game-config/files' })) || [];
